/**
 * Card Match SFX module — mp3-backed buffer cache, mirrors the Anagram /
 * WhatIs pattern. Logical cue methods load mp3s from src/assets/sfx and route
 * through audioBus.sfxGain().
 *
 * Cues:
 *   cardFlip    — chat reveals a tile pair
 *   match       — pair matched (bright chime + sparkle)
 *   mismatch    — pair didn't match (soft "nope")
 *   roundStart  — energetic reveal swoop on round open
 *   roundWin    — celebratory fanfare on all-matched
 *   timeout     — somber sting on round timeout
 *   tickWarning — final-10s urgency tick (per-second)
 *   scoreChime  — subtle scoring soundlet on point award
 */

import cardFlipUrl from '../assets/sfx/card-flip.mp3?url';
import matchUrl from '../assets/sfx/match.mp3?url';
import mismatchUrl from '../assets/sfx/mismatch.mp3?url';
import roundStartUrl from '../assets/sfx/round-start.mp3?url';
import roundWinUrl from '../assets/sfx/round-win.mp3?url';
import timeoutUrl from '../assets/sfx/timeout.mp3?url';
import tickWarningUrl from '../assets/sfx/tick-warning.mp3?url';
import scoreChimeUrl from '../assets/sfx/score-chime.mp3?url';

import { audioBus } from './bus.js';

export type SfxName =
  | 'cardFlip'
  | 'match'
  | 'mismatch'
  | 'roundStart'
  | 'roundWin'
  | 'timeout'
  | 'tickWarning'
  | 'scoreChime';

const URL_MAP: Record<SfxName, string> = {
  cardFlip: cardFlipUrl,
  match: matchUrl,
  mismatch: mismatchUrl,
  roundStart: roundStartUrl,
  roundWin: roundWinUrl,
  timeout: timeoutUrl,
  tickWarning: tickWarningUrl,
  scoreChime: scoreChimeUrl,
};

const buffers = new Map<SfxName, AudioBuffer | null>();
const inflight = new Map<SfxName, Promise<AudioBuffer | null>>();

async function loadBuffer(name: SfxName): Promise<AudioBuffer | null> {
  if (buffers.has(name)) return buffers.get(name) ?? null;
  const existing = inflight.get(name);
  if (existing) return existing;
  const ctx = audioBus.context();
  if (!ctx) return null;
  const url = URL_MAP[name];
  const p = (async () => {
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const arr = await res.arrayBuffer();
      const buf = await ctx.decodeAudioData(arr);
      buffers.set(name, buf);
      return buf;
    } catch (err) {
      console.warn(`[cm-sfx] failed to load ${name} (${url}):`, err);
      buffers.set(name, null);
      return null;
    } finally {
      inflight.delete(name);
    }
  })();
  inflight.set(name, p);
  return p;
}

function playBuffer(ctx: AudioContext, buf: AudioBuffer, name: SfxName): void {
  try {
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.connect(audioBus.sfxGain());
    src.start(0);
  } catch (err) {
    console.warn(`[cm-sfx] play ${name} failed:`, err);
  }
}

function play(name: SfxName): void {
  const ctx = audioBus.context();
  if (!ctx) return;
  const buf = buffers.get(name);
  if (!buf) {
    void loadBuffer(name).then((b) => {
      if (b && audioBus.isUnlocked()) playBuffer(ctx, b, name);
    });
    return;
  }
  if (!audioBus.isUnlocked()) {
    void audioBus.unlock();
  }
  playBuffer(ctx, buf, name);
}

const ALL_NAMES: SfxName[] = [
  'cardFlip',
  'match',
  'mismatch',
  'roundStart',
  'roundWin',
  'timeout',
  'tickWarning',
  'scoreChime',
];

export const sfx = {
  async preload(): Promise<void> {
    await Promise.all(ALL_NAMES.map((n) => loadBuffer(n)));
  },
  play,
  cardFlip(): void {
    play('cardFlip');
  },
  match(): void {
    play('match');
  },
  mismatch(): void {
    play('mismatch');
  },
  roundStart(): void {
    play('roundStart');
  },
  roundWin(): void {
    play('roundWin');
  },
  timeout(): void {
    play('timeout');
  },
  tickWarning(): void {
    play('tickWarning');
  },
  scoreChime(): void {
    play('scoreChime');
  },
};
